import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import {
  IconDroplet,
  IconAlertTriangle,
  IconArrowRight,
  IconCheck,
  IconBuilding,
} from '@tabler/icons-react';

export default function FuelManagerDashboard() {
  const { userProfile, org, orgId } = useAuth();
  const navigate = useNavigate();
  const [branchTanks, setBranchTanks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) {
        setLoading(false);
        return;
      }
      try {
        const branchesSnap = await getDocs(collection(db, 'organisations', orgId, 'branches'));
        const branches = branchesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const enriched = await Promise.all(branches.map(async (branch) => {
          const tanksSnap = await getDocs(
            collection(db, 'organisations', orgId, 'branches', branch.id, 'dervTanks')
          );
          const tanks = tanksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          return { ...branch, tanks };
        }));

        setBranchTanks(enriched.filter(b => b.tanks.length > 0));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const brandColour = org?.brandColour || 'var(--color-brand)';

  const allTanks = branchTanks.flatMap(b => b.tanks.map(t => ({
    ...t,
    branchName: b.name,
    branchId: b.id,
    pct: Math.round((t.currentLitres / t.capacityLitres) * 100),
  })));

  const lowTanks = allTanks.filter(t => t.pct <= (t.orderThresholdPct || 15));
  const criticalTanks = allTanks.filter(t => t.pct <= 10);
  const totalLitres = allTanks.reduce((sum, t) => sum + (t.currentLitres || 0), 0);
  const totalCapacity = allTanks.reduce((sum, t) => sum + (t.capacityLitres || 0), 0);
  const groupPct = totalCapacity > 0 ? Math.round((totalLitres / totalCapacity) * 100) : 0;

  const tankColour = (pct) => pct <= 10 ? 'var(--color-danger)' : pct <= 30 ? 'var(--color-warning)' : 'var(--color-success)';
  const tankBg = (pct) => pct <= 10 ? 'var(--color-danger-light)' : pct <= 30 ? 'var(--color-warning-light)' : 'var(--color-success-light)';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', minHeight: '100vh' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>
            {greeting}, {userProfile?.name?.split(' ')[0] || 'there'}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{dateStr}</p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            background: brandColour + '18',
            color: brandColour,
            borderRadius: 'var(--radius-full)',
            padding: '3px 10px',
            marginTop: 8,
          }}>
            <IconDroplet size={12} stroke={1.5} />
            {org?.name || 'Fuel overview'} · All branches
          </div>
        </div>

        {/* Summary stats */}
        {!loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Total tanks', value: allTanks.length, colour: 'var(--color-success)', icon: IconDroplet },
              { label: 'Low tanks', value: lowTanks.length, colour: lowTanks.length > 0 ? 'var(--color-warning)' : 'var(--color-success)', icon: IconAlertTriangle },
              { label: 'Critical tanks', value: criticalTanks.length, colour: criticalTanks.length > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconAlertTriangle },
              { label: 'Group fuel level', value: `${groupPct}%`, colour: tankColour(groupPct), icon: IconDroplet },
            ].map(stat => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 36, fontWeight: 400, color: stat.colour, lineHeight: 1 }}>{stat.value}</div>
                    <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: stat.colour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={16} color={stat.colour} stroke={1.5} />
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{stat.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Branch tanks */}
        <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>All tanks</div>

        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: '32px 0', textAlign: 'center' }}>Loading tanks...</div>
        ) : branchTanks.length === 0 ? (
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '48px 32px', textAlign: 'center' }}>
            <IconDroplet size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 6 }}>No tanks yet</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>Tanks will appear here once branch managers add them during onboarding</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12 }}>
            {branchTanks.map(branch => (
              branch.tanks.map(tank => {
                const pct = Math.round((tank.currentLitres / tank.capacityLitres) * 100);
                const isLow = pct <= (tank.orderThresholdPct || 15);
                const isCritical = pct <= 10;
                return (
                  <div
                    key={tank.id}
                    onClick={() => navigate(`/fuel/${tank.id}`)}
                    style={{
                      background: 'var(--color-bg-primary)',
                      border: `0.5px solid ${isCritical ? 'var(--color-danger)' : isLow ? 'var(--color-warning)' : 'var(--color-border-light)'}`,
                      borderRadius: 'var(--radius-lg)',
                      padding: '18px 20px',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => !isCritical && !isLow && (e.currentTarget.style.borderColor = 'var(--color-border-medium)')}
                    onMouseLeave={e => !isCritical && !isLow && (e.currentTarget.style.borderColor = 'var(--color-border-light)')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: brandColour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconBuilding size={16} color={brandColour} stroke={1.5} />
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)' }}>{branch.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{tank.name}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, background: tankBg(pct), color: tankColour(pct), display: 'flex', alignItems: 'center', gap: 4 }}>
                        {isCritical || isLow ? <IconAlertTriangle size={10} stroke={1.5} /> : <IconCheck size={10} stroke={1.5} />}
                        {isCritical ? 'Critical' : isLow ? 'Order required' : 'OK'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 32, fontWeight: 400, color: tankColour(pct) }}>{pct}%</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'right' }}>
                        <div>{tank.currentLitres?.toLocaleString()}L remaining</div>
                        <div>of {tank.capacityLitres?.toLocaleString()}L capacity</div>
                      </div>
                    </div>

                    <div style={{ height: 8, background: 'var(--color-bg-secondary)', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: tankColour(pct), borderRadius: 4, transition: 'width 0.5s ease' }} />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                      <span>~{Math.round(tank.currentLitres / 120)} days remaining</span>
                      <span onClick={e => { e.stopPropagation(); navigate(`/fuel/${tank.id}`); }} style={{ color: brandColour, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        View <IconArrowRight size={12} stroke={1.5} />
                      </span>
                    </div>
                  </div>
                );
              })
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}